import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { lineTotals, resolveRate, type LineTotals } from '@/app/lib/pricing';
import type { PricedItem, Unit } from '@/app/types/item';
import type { SalesOrder, SalesOrderItemCreate } from '@/app/types/salesOrder';

/** A line as the editor holds it, before rate resolution. */
interface EditorEntry {
    itemId: number;
    name: string;
    unitId: number;
    unitName: string;
    taxRateId: number | null;
    taxPercent: number;
    isTaxInclusive: boolean;
    quantity: number;
    /** Quantity the order was saved with. Null for lines added in this session. */
    savedQuantity: number | null;
    /** Rate the order was saved with. Null for lines added in this session. */
    savedRate: number | null;
    /** standard_rate the order was saved with; null if none was stored. */
    savedStandardRate: number | null;
    /** Live catalog entry, absent when the product is no longer active. */
    priced: PricedItem | null;
}

export interface EditorLine extends EditorEntry {
    rate: number;
    standardRate: number | null;
    tierLabel: string | null;
    totals: LineTotals;
    /** True once the quantity differs from what the office last saw. */
    changed: boolean;
    /** True when the quantity change also moved the line onto a different rate. */
    repriced: boolean;
}

const toNumber = (value: string | number | null | undefined): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

/**
 * Editing state for an existing sales order's lines.
 *
 * Rates are quantity-dependent in this catalogue, so a line whose quantity the
 * manager changes is re-priced against the live ladder — leaving 300 units at the
 * 50-unit rate would undercharge exactly the orders bulk pricing exists for. A line
 * left alone keeps the rate the order was saved with, so an edit can never silently
 * reprice lines nobody touched, however much the catalogue has moved since.
 *
 * Products that have since been deactivated have no live pricing to resolve against;
 * those lines keep their saved rate whatever happens to the quantity.
 *
 * `pricedByItemId` must be quoted for this order (document_id), so re-priced lines
 * use the pricing mode the order was created in, not the party's current one.
 */
export function useOrderEditor(
    order: SalesOrder | null,
    pricedByItemId: Map<number, PricedItem> | null,
    units: Unit[] | undefined
) {
    const [entries, setEntries] = useState<EditorEntry[]>([]);
    // Re-seed only when a different order (or its freshly saved copy) arrives —
    // not on every render that happens to produce a new map identity.
    const seededFor = useRef<string | null>(null);

    const seedKey = order
        ? `${order.id}:${order.updated_at ?? ''}:${order.items?.length ?? 0}:${pricedByItemId ? 'priced' : 'raw'}`
        : null;

    useEffect(() => {
        if (!order || !seedKey) return;
        if (seededFor.current === seedKey) return;
        seededFor.current = seedKey;

        const unitById = new Map((units ?? []).map((u) => [u.id, u]));

        setEntries(
            (order.items ?? []).map((line): EditorEntry => {
                const priced = pricedByItemId?.get(line.item_id) ?? null;
                const quantity = toNumber(line.quantity);
                const rate = toNumber(line.rate);
                const savedStandardRate =
                    line.standard_rate == null ? null : toNumber(line.standard_rate);
                return {
                    itemId: line.item_id,
                    name: priced?.name ?? line.item?.name ?? `Item #${line.item_id}`,
                    unitId: line.unit_id,
                    unitName:
                        priced?.unitName ?? unitById.get(line.unit_id)?.name ?? 'Unit',
                    taxRateId: line.tax_rate_id ?? priced?.tax_rate_id ?? null,
                    taxPercent: priced?.taxPercent ?? 0,
                    isTaxInclusive: priced?.isTaxInclusive ?? true,
                    quantity,
                    savedQuantity: quantity,
                    savedRate: rate,
                    savedStandardRate,
                    priced,
                };
            })
        );
    }, [order, seedKey, pricedByItemId, units]);

    const setQuantity = useCallback((itemId: number, quantity: number) => {
        setEntries((prev) => {
            if (quantity <= 0) return prev.filter((e) => e.itemId !== itemId);
            return prev.map((e) => (e.itemId === itemId ? { ...e, quantity } : e));
        });
    }, []);

    /** Add a product, or bump it if it is already on the order. */
    const addItem = useCallback((item: PricedItem, quantity: number) => {
        setEntries((prev) => {
            const existing = prev.find((e) => e.itemId === item.id);
            if (existing) {
                if (quantity <= 0) return prev.filter((e) => e.itemId !== item.id);
                return prev.map((e) => (e.itemId === item.id ? { ...e, quantity } : e));
            }
            if (quantity <= 0) return prev;
            return [
                ...prev,
                {
                    itemId: item.id,
                    name: item.name,
                    unitId: item.base_unit_id,
                    unitName: item.unitName,
                    taxRateId: item.tax_rate_id ?? null,
                    taxPercent: item.taxPercent,
                    isTaxInclusive: item.isTaxInclusive,
                    quantity,
                    savedQuantity: null,
                    savedRate: null,
                    savedStandardRate: null,
                    priced: item,
                },
            ];
        });
    }, []);

    const getQuantity = useCallback(
        (itemId: number) => entries.find((e) => e.itemId === itemId)?.quantity ?? 0,
        [entries]
    );

    const reset = useCallback(() => {
        seededFor.current = null;
        setEntries([]);
    }, []);

    const lines = useMemo<EditorLine[]>(
        () =>
            entries.map((entry) => {
                const untouched =
                    entry.savedRate != null && entry.quantity === entry.savedQuantity;

                let rate = entry.savedRate ?? 0;
                let standardRate = entry.savedStandardRate;
                let tierLabel: string | null = null;

                if (!untouched && entry.priced) {
                    const resolved = resolveRate(entry.priced, entry.quantity);
                    if (resolved.priced) {
                        rate = resolved.rate;
                        standardRate = resolved.standardRate;
                        tierLabel = resolved.tierLabel;
                    }
                }

                return {
                    ...entry,
                    rate,
                    standardRate,
                    tierLabel,
                    changed: entry.savedQuantity !== entry.quantity,
                    repriced:
                        entry.savedRate != null &&
                        entry.savedQuantity !== entry.quantity &&
                        rate !== entry.savedRate,
                    totals: lineTotals(
                        entry.quantity,
                        rate,
                        entry.taxPercent,
                        entry.isTaxInclusive
                    ),
                };
            }),
        [entries]
    );

    const grandTotal = useMemo(
        () => lines.reduce((sum, line) => sum + line.totals.totalAmount, 0),
        [lines]
    );

    /**
     * Whether anything would actually change on save.
     *
     * Compared against the order as loaded rather than against a snapshot of the
     * seeded entries, so a removed line still counts as a change after the list has
     * been re-ordered by additions.
     */
    const isDirty = useMemo(() => {
        const saved = order?.items ?? [];
        if (saved.length !== lines.length) return true;
        const byItemId = new Map(lines.map((l) => [l.itemId, l]));
        return saved.some((line) => {
            const current = byItemId.get(line.item_id);
            return !current || current.quantity !== toNumber(line.quantity);
        });
    }, [order, lines]);

    /** The order's final line set, in the shape PATCH expects. */
    const toPayload = useCallback(
        (): SalesOrderItemCreate[] =>
            lines.map((line) => ({
                item_id: line.itemId,
                quantity: String(line.quantity),
                unit_id: line.unitId,
                rate: String(line.rate),
                ...(line.standardRate != null ? { standard_rate: String(line.standardRate) } : {}),
                ...(line.taxRateId ? { tax_rate_id: line.taxRateId } : {}),
            })),
        [lines]
    );

    return {
        lines,
        grandTotal,
        isDirty,
        setQuantity,
        addItem,
        getQuantity,
        reset,
        toPayload,
    };
}
